import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { logger } from '../../utils/logger';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const CookieBanner = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('ccic_cookie_consent');
        if (!consent) {
            // Small delay to show after load
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Helper to push to GTM dataLayer
    const pushToDataLayer = (consentType: string) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'cookie_consent_update',
            consent_type: consentType
        });

        // If 'all' is accepted, we can trigger a specific event for tracking initialization
        if (consentType === 'all') {
            window.dataLayer.push({
                event: 'tracking_consent_granted'
            });
        }
    };

    const handleAcceptAll = () => {
        localStorage.setItem('ccic_cookie_consent', 'all');
        window.dispatchEvent(new Event('cookie_consent_updated'));
        pushToDataLayer('all');
        setIsVisible(false);
    };

    const handleAcceptNecessary = () => {
        localStorage.setItem('ccic_cookie_consent', 'necessary');
        window.dispatchEvent(new Event('cookie_consent_updated'));
        pushToDataLayer('necessary');
        setIsVisible(false);
    };

    const handleCustomize = () => {
        // For now, treat as necessary or implement a modal later
        logger.info("Customize preferences clicked");
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
                >
                    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-2xl border border-gray-200 p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center md:items-start">

                        {/* Text Content */}
                        <div className="flex-1 text-sm text-gray-600 space-y-4">
                            <p>
                                We and our partners use cookies on this website for different purposes. We use strictly necessary cookies that are essential for basic website functionality and cannot be switched off.
                            </p>
                            <p>
                                We also use other cookies such as preference, performance, and marketing cookies to provide you with the best possible digital experience, enhance the performance of this website, optimize our services, and to show you relevant information.
                            </p>
                            <p>
                                Accept all cookies allow us to use cookies for the purposes stated above. Customize your preferences to choose which cookies you want us to enable. Discover more in our <Link to="/privacy-policy" className="text-[#0055a0] underline font-medium hover:text-[#004080]">Cookies Notice</Link>. Learn how we collect, use, and share your personal data in our <Link to="/privacy-policy" className="text-[#0055a0] underline font-medium hover:text-[#004080]">Privacy Policy</Link> and about the conditions to use our site in our <Link to="/terms" className="text-[#0055a0] underline font-medium hover:text-[#004080]">Terms of Use</Link>.
                            </p>
                        </div>

                        {/* Buttons */}
                        <div className="w-full md:w-80 flex-shrink-0 flex flex-col gap-3">
                            <button
                                onClick={handleCustomize}
                                className="w-full px-4 py-3 bg-white border-2 border-[#0055a0] text-[#0055a0] font-bold rounded hover:bg-gray-50 transition-colors text-sm"
                            >
                                Customize preferences
                            </button>

                            <button
                                onClick={handleAcceptNecessary}
                                className="w-full px-4 py-3 bg-[#0055a0] border-2 border-[#0055a0] text-white font-bold rounded hover:bg-[#004080] hover:border-[#004080] transition-colors text-sm"
                            >
                                Accept strictly necessary only
                            </button>

                            <button
                                onClick={handleAcceptAll}
                                className="w-full px-4 py-3 bg-[#0055a0] border-2 border-[#0055a0] text-white font-bold rounded hover:bg-[#004080] hover:border-[#004080] transition-colors text-sm"
                            >
                                Accept all cookies
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieBanner;
